<script setup lang="ts">
import { useFlowControlStore } from '@components/flow-controller' // TODO: migrate off deprecated package
import { h } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useModalStore } from '@tix/modal'
import { APIErrorCodes, type ApplicationError, isNotFoundError } from '@tix/openapi-sdk'

import { setDocumentTitle } from '@/helpers/documentTitle'
import { getCartTrackableData } from '@/helpers/errorContext'
import { keepShoppingRedirect, locationAssign, locationReload } from '@/helpers/navigation'
import { usePageShowPersisted } from '@/helpers/pageShowComposables'
import { withSellerPrefix } from '@/helpers/sellerPrefix'

import { API } from '@/services/API'
import { errorTracker } from '@/services/errorTracking'

import { useCartStore } from '@/stores/cart'
import { useGlobalError } from '@/stores/useGlobalErrorStore'

import { provideBundleStepContext } from '@/composables/useBundleStepTracking'
import { useFlowGa4Tracking } from '@/composables/useFlowGa4Tracking'
import { useIncompleteBundleLeaveModal } from '@/composables/useIncompleteBundleLeaveModal'

import FlowDetailsView, { type FlowDetailsViewProps } from './FlowDetailsView.vue'

type Props = {
  id: string
  /** Optional cart id handed off from another app (e.g. web-client) to resume an existing cart instead of creating a new one. */
  cartId?: string
} & Pick<FlowDetailsViewProps, 'isDetailedMode'>

const props = defineProps<Props>()

const cartStore = useCartStore()
const globalError = useGlobalError()
const modalStore = useModalStore()
const flowStore = useFlowControlStore()
const route = useRoute()
const router = useRouter()

const [cartResponse, flowDefinitionResponse] = await Promise.all([
  props.cartId ? cartStore.getById(props.cartId) : cartStore.get(),
  API.get('/pub/flow/definition/{id}', { id: props.id }),
])

if (props.cartId) {
  // Strip the consumed cartId from the URL so a refresh or shared link doesn't re-trigger the
  // handoff (or leak the cart id). Other query params (e.g. detail) are preserved.
  const query = { ...route.query }
  delete query.cartId
  router.replace({ query })
}

const loadedCartId = cartResponse.status === 'success' ? cartResponse.data.id : null

if (!loadedCartId) {
  const error = new Error('Error fetching cart', { cause: cartResponse.error })
  errorTracker.track(error, {
    operation: 'cart.get',
    flowDefinitionId: props.id,
    cart: getCartTrackableData(cartResponse.data),
  })
  globalError.set('Unable to load cart', { dismissable: false })
}

const flowDefinition =
  flowDefinitionResponse.status === 'success' ? flowDefinitionResponse.data.flow_definition._data?.[0] : null

if (!flowDefinition?.id) {
  const err = flowDefinitionResponse.status === 'error' ? flowDefinitionResponse.error : null

  if (isNotFoundError(err)) {
    globalError.set('Flow definition not found', { dismissable: false })
  } else {
    const error = new Error('Error fetching flow definition', { cause: err })
    errorTracker.track(error, {
      operation: 'flow_definition.get',
      flowDefinitionId: props.id,
      flowDefinitionData: flowDefinitionResponse.data,
    })
    globalError.set('An unexpected error occurred', { dismissable: false })
  }
}

if (flowDefinition?.name) setDocumentTitle(flowDefinition.name)

if (flowDefinition) {
  provideBundleStepContext(flowDefinition)
  useFlowGa4Tracking(flowDefinition)
}

const ready = Boolean(loadedCartId && flowDefinition?.id)

useIncompleteBundleLeaveModal()

const handleError = (error: ApplicationError) => {
  if (error.handled) return

  const apiError = error.error
  const cartExpired = isNotFoundError(apiError) && apiError.hasCode(APIErrorCodes.cart_not_found)
  if (cartExpired) return cartStore.expire()
}

const onCheckout = () => {
  // Checkout on apps/web-client/src/routes/CheckoutRoute.vue
  locationAssign(withSellerPrefix(`/checkout/${cartStore.cart?.id}`))
}

const onKeepShopping = () => {
  const cartId = cartStore.cart?.id
  // The button renders whether or not there is a cart, so without one the click is a dead end.
  if (!cartId) {
    return errorTracker.track(new Error('Keep shopping clicked with no cart to hand off'), {
      operation: 'keep-shopping',
      flowDefinitionId: props.id,
    })
  }

  keepShoppingRedirect(cartId)
}

/**
 * We don't currently support editing a flow after the user has proceeded to checkout and the cart has been populated.
 *
 * When the user navigates BACK from the checkout page to the flow details page, we block interactions and present a modal prompting the user to continue to out or start over.
 */
usePageShowPersisted(() => {
  if (!cartStore.cart?.id) {
    errorTracker.track(new Error('Cart not found after back-navigation'), { operation: 'back-navigation' })
    return locationReload()
  }

  if (flowStore.isComplete) {
    modalStore.open({
      component: h(
        'div',
        "You'll have to start over to make changes. Otherwise your order is ready and waiting at checkout.",
      ),
      name: 'require-checkout',
      title: 'Checkout',
      size: 'sm',
      closeable: false,
      dismissible: false,
      actions: [
        {
          hierarchy: 'secondary',
          label: 'Start over',
          onAction: () => locationReload(),
        },
        {
          hierarchy: 'primary',
          label: 'Checkout',
          onAction: () => onCheckout(),
        },
      ],
      // force a reload if the user browser forces closes the modal
      onClose: () => locationReload(),
    })
  }
})
</script>
<template>
  <FlowDetailsView
    v-if="ready"
    :id="props.id"
    :cartId="loadedCartId!"
    :flowDefinition="flowDefinition!"
    :isDetailedMode="props.isDetailedMode"
    @checkout="onCheckout"
    @error="handleError"
    @keep-shopping="onKeepShopping"
  />
</template>
